import { ComponentProps, createElement, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { Alert, Box, ChevronDownIcon, HStack, IIconProps, Popover, Pressable, Spinner, VStack, useTheme, useToast } from 'native-base';
import { isEmpty, reduce, size, some } from 'lodash';

import { CloseIcon, Modal, RefreshCCW03, RefreshIcon, ReverseLeftIcon, Text, ZapFastIcon } from '@pimm/base';
import { PositionTypeEnum, SmsWorkforceApi } from '@pimm/services/lib/sms-workforce';
import { formatToISOString, stringToDateLocal } from '@app/utils/date-formatter';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { DayBlock, useSiteTime } from '@app/features/store-core';
import { useGetPositionScheduleLive } from '../hooks';
import { usePositioningPlan } from '../context';
import { PositioningSlot } from '../reducers';
import { ConfirmReverseTemplatePlan } from './confirm-reverse-template-plan';

enum AutoAssignEvent {
  CurrentTab,
  AllTabs,
  Reset,
  Reverse,
  Load,
  Clear,
}

type PositioningDropdownOptionsProps = {
  _container?: ComponentProps<typeof HStack>;
  dayBlock: DayBlock;
  hideOptions?: boolean;
  isDisabled?: boolean;
  liveSchedules: ReturnType<typeof useGetPositionScheduleLive>[0];
  siteId: string;
};

export const PositioningDropdownOptions = ({
  _container,
  dayBlock,
  hideOptions,
  liveSchedules,
  siteId,
  ...props
}: PositioningDropdownOptionsProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const toast = useToast();
  const siteTime = useSiteTime();
  const isOpenDropdown = useBoolean();
  const modalConfirm = useModalFocus();
  const { changes, positioning } = usePositioningPlan();
  const [menuItems] = useState<{ type: AutoAssignEvent; icon: React.ElementType; isDisabled?: boolean; label: string }[]>([
    { type: AutoAssignEvent.AllTabs, icon: ZapFastIcon, label: 'positioning_plan:auto_assign_all_plans' },
    {
      type: AutoAssignEvent.Load,
      icon: RefreshCCW03,
      label: 'positioning_plan:load_default_plan',
    },
    { type: AutoAssignEvent.Clear, icon: CloseIcon, label: 'positioning_plan:clear_assignments' },
    { type: AutoAssignEvent.Reset, icon: RefreshIcon, label: 'positioning_plan:reset_current_plan' },
    { type: AutoAssignEvent.Reverse, icon: ReverseLeftIcon, label: 'positioning_plan:restore_template_plan' },
  ]);

  const confirmAndAssignGroup = useMutation({
    mutationFn: SmsWorkforceApi.ConfirmAndAssignGroup,
    onSuccess: () => {
      liveSchedules.refetch();
    },
    onError: () => {
      return toast.show({
        placement: 'top',
        render: () => (
          <Alert maxWidth="100%" alignSelf="center" flexDirection="row" status="error" variant="subtle">
            <VStack space={1} flexShrink={1} w="100%">
              <HStack space={2} flexShrink={1} alignItems="center">
                <Alert.Icon size="md" />
                <Box>
                  <Text size="lg" flexShrink={1} fontWeight={700} color="black" lineHeight="xs">
                    {t('positioning_plan:confirmation_failed')}
                  </Text>
                  <Text size="md" color="gray.900">
                    {t('positioning_plan:error_try_again')}
                  </Text>
                </Box>
              </HStack>
            </VStack>
          </Alert>
        ),
      });
    },
  });

  const confirmPositionGroup = useMutation({
    mutationFn: SmsWorkforceApi.ConfirmPositionGroup,
    onSuccess: () => liveSchedules.refetch(),
    onError: () => {
      return toast.show({
        placement: 'top',
        render: () => (
          <Alert maxWidth="100%" alignSelf="center" flexDirection="row" status="error" variant="subtle">
            <VStack space={1} flexShrink={1} w="100%">
              <HStack space={2} flexShrink={1} alignItems="center">
                <Alert.Icon size="md" />
                <Box>
                  <Text size="lg" flexShrink={1} fontWeight={700} color="black" lineHeight="xs">
                    {t('positioning_plan:confirmation_failed')}
                  </Text>
                  <Text size="md" color="gray.900">
                    {t('positioning_plan:error_try_again')}
                  </Text>
                </Box>
              </HStack>
            </VStack>
          </Alert>
        ),
      });
    },
  });

  const resetConfirmAndAssignByDayBlock = useMutation({
    mutationFn: SmsWorkforceApi.ResetConfirmAndAssignByDayBlock,
    onSuccess: () => {
      liveSchedules.refetch();
    },
  });

  const resetConfirmGroup = useMutation({
    mutationFn: SmsWorkforceApi.ResetConfirmGroup,
    onSuccess: () => liveSchedules.refetch(),
  });

  const isConfirmed = !!positioning?.confirmedTime;
  const hasAvailableEmployees = some(
    positioning?.employees,
    _ => !some<PositioningSlot>(positioning?.positionSlots, slot => slot.assignee?.employeeId === _.employeeId),
  );
  const hasAssignees = some(positioning?.positionSlots, _ => !!_.assignee?.employeeId);
  const hasShiftSubBlocks = size(positioning?.shiftSubBlocks) > 1;
  const hasRestoreTemplate = some(positioning?.positionSlots, _ => !!_.origPositionId && _.positionId !== _.origPositionId);

  const resetDayblock = useMutation({ mutationFn: SmsWorkforceApi.ResetPositionGroup, onSuccess: () => liveSchedules.refetch() });

  const isDisabled = props.isDisabled || changes.length > 0;
  const isLoading =
    confirmAndAssignGroup.isLoading ||
    confirmPositionGroup.isLoading ||
    resetConfirmGroup.isLoading ||
    resetConfirmAndAssignByDayBlock.isLoading ||
    resetDayblock.isLoading;

  const handlePressAction = (type: AutoAssignEvent) => async () => {
    if (isLoading) return;

    let confirmDateTime = dayBlock.startTime;
    let currentDateTime = dayBlock.startTime;
    const today = siteTime.today();
    const subBlockDateTime = stringToDateLocal(liveSchedules.data?.positionGroup?.subBlockTime);
    const nonServiceEmployees = reduce(
      positioning?.positionSlots,
      (employeeIds: string[], _) => {
        if (!_.id && _.positionType === PositionTypeEnum.NonService && _.assignee?.employeeId) {
          return [...employeeIds, _.assignee.employeeId];
        }
        return employeeIds;
      },
      [],
    );

    // Check if today, use the current time when making a POST to the confirm endpoint.
    if (today >= dayBlock.startTime && today < dayBlock.endTime) {
      confirmDateTime = today;
      currentDateTime = today;
    }

    // confirmDateTime should be equal to the subBlockDateTime for all suBlocks set in the future.
    if (subBlockDateTime && confirmDateTime < subBlockDateTime) {
      confirmDateTime = subBlockDateTime;
    }

    isOpenDropdown.setFalse();

    if (type === AutoAssignEvent.CurrentTab) {
      // Confirm and auto-assign
      confirmAndAssignGroup.mutate({
        confirmDateTime: formatToISOString(confirmDateTime),
        siteId: siteId,
        isLocked: true,
        nonServiceEmployeeIds: nonServiceEmployees,
        subBlockDateTime: subBlockDateTime ? formatToISOString(subBlockDateTime) : undefined,
      });
    } else if (type === AutoAssignEvent.AllTabs) {
      // Reset and confirm all tabs
      resetConfirmAndAssignByDayBlock.mutate({
        currentDateTime: formatToISOString(currentDateTime),
        siteId: siteId,
        assignOnly: true,
        resetOnly: false,
      });
    } else if (type === AutoAssignEvent.Reset) {
      // Reset position group
      resetDayblock.mutate({ positionGroupId: positioning?.id });
    } else if (type === AutoAssignEvent.Reverse) {
      // Reverse positions back to original names
      isOpenDropdown.setFalse();
      modalConfirm.setOpen();
    } else if (type === AutoAssignEvent.Load) {
      confirmPositionGroup.mutate({
        confirmDateTime: formatToISOString(confirmDateTime),
        isLocked: true,
        nonServiceEmployeeIds: nonServiceEmployees,
        siteId: siteId,
        subBlockDateTime: subBlockDateTime ? formatToISOString(subBlockDateTime) : undefined,
      });
    } else if (type === AutoAssignEvent.Clear) {
      // Reset confirm position group
      resetConfirmGroup.mutate({
        confirmDateTime: formatToISOString(confirmDateTime),
        positionGroupId: positioning?.id,
      });
    }
  };

  return (
    <>
      <Popover
        isKeyboardDismissable
        offset={5}
        isOpen={(hasAvailableEmployees || hasShiftSubBlocks || hasRestoreTemplate) && isOpenDropdown.value}
        onClose={isOpenDropdown.setFalse}
        trigger={triggerProps => (
          <Pressable
            rounded="lg"
            h={9}
            bg="primary.500"
            isDisabled={isDisabled}
            opacity={isDisabled || isLoading ? 0.5 : 1}
            onPress={handlePressAction(hasAvailableEmployees ? AutoAssignEvent.CurrentTab : AutoAssignEvent.Reset)}
          >
            <HStack rounded="lg" alignItems="center" h="full">
              <HStack space={1} alignItems="center" justifyContent="center" px={2} h="full" minW="60px" {..._container}>
                {!isConfirmed || hasAvailableEmployees ? (
                  <>{hideOptions && isLoading ? <Spinner size={16} color="black" /> : <ZapFastIcon size={5} color="black" />}</>
                ) : (
                  <RefreshIcon size={4} color="black" />
                )}
                <Text size="md" fontWeight={600} color="black">
                  {!isConfirmed || hasAvailableEmployees ? t('positioning_plan:auto_assign') : t('positioning_plan:reset')}
                </Text>
              </HStack>

              {!hideOptions && (
                <Pressable
                  alignItems="center"
                  justifyContent="center"
                  px={1}
                  minW="36px"
                  h="full"
                  {...triggerProps}
                  disabled={isLoading}
                  borderLeftWidth={1}
                  borderLeftColor="primary.100"
                  onPress={isOpenDropdown.setTrue}
                >
                  {isLoading ? <Spinner size={16} color="black" /> : <ChevronDownIcon size="14px" color="black" />}
                </Pressable>
              )}
            </HStack>
          </Pressable>
        )}
      >
        <Popover.Content accessibilityLabel="Account Dropdown" mr={3} borderWidth={0} shadow="7" minWidth={260} maxWidth={320}>
          <Popover.Body p={0} w="full" bgColor="white" minWidth="200px">
            <Box bgColor="gray.25">
              {menuItems.map(menu => {
                // If shiftSubBlocks is less or equal to 1, hide "Auto Assign All Tabs"
                if (menu.type === AutoAssignEvent.AllTabs && !hasShiftSubBlocks) return undefined;
                if (menu.type === AutoAssignEvent.Reverse && !hasRestoreTemplate) return undefined;
                if (menu.type === AutoAssignEvent.Load && !isEmpty(positioning?.positionSlots)) return undefined;
                if (menu.type === AutoAssignEvent.Clear && !hasAssignees) return undefined;
                if (menu.type === AutoAssignEvent.Reset && !positioning?.id) return undefined;

                return (
                  <Pressable
                    key={menu.label}
                    isDisabled={isDisabled}
                    opacity={isDisabled || isLoading ? 0.5 : 1}
                    onPress={handlePressAction(menu.type)}
                  >
                    {({ isHovered }) => {
                      const color = isDisabled ? colors.gray[400] : isHovered ? colors.black : colors.gray[800];
                      return (
                        <HStack space={2} alignItems="center" py={1} px={3} minH={10} bg={isHovered ? 'gray.50' : undefined}>
                          {createElement<IIconProps>(menu.icon, {
                            color: color,
                            size: menu.type === AutoAssignEvent.Reverse ? '14px' : '16px',
                          })}
                          <Box flex={1}>
                            <Text
                              size="md"
                              fontWeight={isHovered ? 600 : 500}
                              color={color}
                              textTransform="capitalize"
                              lineHeight="xs"
                              numberOfLines={2}
                            >
                              {t(menu.label)}
                            </Text>
                          </Box>
                        </HStack>
                      );
                    }}
                  </Pressable>
                );
              })}
            </Box>
          </Popover.Body>
        </Popover.Content>
      </Popover>

      <Modal
        hideClose
        isOpen={modalConfirm.isOpen}
        onClose={modalConfirm.setHide}
        size="md"
        _content={{ rounded: 'xl', p: 5, pt: 4, maxW: 400 }}
      >
        <ConfirmReverseTemplatePlan
          blockStartTime={dayBlock.startTime}
          siteId={positioning?.siteId}
          onClose={modalConfirm.setHide}
          onSuccess={liveSchedules.refetch}
        />
      </Modal>
    </>
  );
};
